import { ScheduledTask, ScheduledTaskIntervalOverrides } from '../types/scheduling/SchedulingTypes';
import { durationTextToMinutes } from './durationMinutes';

type Overrides = Partial<ScheduledTaskIntervalOverrides>;

/**
 * The task's work in minutes: its own length when it was split, otherwise its occasion's.
 *
 * Text in the overrides wins over the minutes, as in the backend. Only an older client writes
 * it, and that client keeps the minutes it read next to the text it rewrote.
 */
export const taskWorkMinutes = (
  task: Pick<ScheduledTask, 'workMinutes' | 'intervalOverrides'>
): number => overrideWorkMinutes(task.intervalOverrides) ?? task.workMinutes ?? 0;

/** The task's work and transport in minutes, a missing transport counting as none. */
export const taskTotalMinutes = (
  task: Pick<ScheduledTask, 'workMinutes' | 'intervalOverrides' | 'transportMinutes'>
): number => taskWorkMinutes(task) + (task.transportMinutes ?? 0);

/** The work of all the tasks in minutes, added up. */
export const sumWorkMinutes = (
  tasks: Pick<ScheduledTask, 'workMinutes' | 'intervalOverrides'>[]
): number => tasks.reduce((sum, task) => sum + taskWorkMinutes(task), 0);

const overrideWorkMinutes = (overrides?: Overrides): number | null | undefined =>
  typeof overrides?.workTime === 'string'
    ? durationTextToMinutes(overrides.workTime)
    : overrides?.workMinutes;

/** The overrides with the task's own work set, and the text that would win over it dropped. */
export const overridesWithWorkMinutes = (
  overrides: Overrides | null | undefined,
  workMinutes: number | null
): Overrides => {
  const { workTime: _workTime, ...rest } = overrides ?? {};
  return { ...rest, workMinutes };
};

/**
 * The overrides as the backend should get them: an older client's text turned into the minutes
 * it means, since the text is dropped before sending.
 */
export const overridesInMinutes = (overrides: Overrides): Overrides =>
  typeof overrides.workTime === 'string'
    ? overridesWithWorkMinutes(overrides, durationTextToMinutes(overrides.workTime))
    : overrides;
